import {cloneElement, Fragment} from '~preact';
import {combineClasses, resolvePolymorphVar} from "@green24/npm-javascript-utils";
import {RIS} from "./ReactIS";
import {AppUtils} from "./AppUtils";

export class ComponentUtils {
	/**
	 * Resolve polymorph prop
	 * ---
	 * Handles prop data based on options.
	 *
	 * **Warning!** All options must be defined according "[type]: () => ..." formula
	 * @param {*} prop
	 * @param {Object} options
	 * @param {*} fallback
	 * @return {*}
	 */
	static resolvePolymorphProp(prop, options, fallback = () => null) {
		return resolvePolymorphVar(
			prop,
			{
				...options,
				object: (o) => {
					if(RIS.component(o)) {
						return RIS.property(options, "component") ? options.component(o) : fallback();
					}

					return RIS.property(options, "object") ? options.object(o) : fallback();
				}
			},
			fallback,
			true
		);
	}

	/**
	 * Propagate props
	 * ---
	 * @param {Component} component
	 * @param {Object} props
	 * @param {boolean} suppressWarning
	 * @return {Component}
	 */
	static propagateProps(component, props, suppressWarning = false) {
		if(!component) return component;
		if(RIS.customComponent(component)) {
			return cloneElement(component, props);
		}
		if(!suppressWarning && AppUtils.isDev) {
			console.warn("Cannot propagate props to component:", component);
		}
		let {className, style} = props;
		return cloneElement(component, {className, style});
	}

	/**
	 * Wrap with component
	 * ---
	 * @param {Component} body
	 * @param {Component} wrapper
	 * @param {Object} props
	 * @return {Comment}
	 */
	static wrapWithComponent(body, wrapper, props = {}) {
		if(wrapper) {
			return cloneElement(wrapper, {
				...props,
				className: combineClasses(props.className, wrapper.props.className),
				style: {
					...wrapper.props.style,
					...props.style
				},
			}, body);
		}
		return body;
	}

	/**
	 * Map items
	 * ---
	 * Simplified [].map((...) => <Fragment key={...}>...</Fragment>)
	 * @param {Array} items
	 * @param {function(item, i, items): React.Component} componentSolver
	 * @param {string} itemKeyForIdentifier
	 * @param {function(item, i, keyPropertyName): string|number} keySolver
	 * @return {React.Component[]}
	 */
	static mapItems(
		items,
		componentSolver,
		{
			itemKeyForIdentifier = "id",
			keySolver = (item, index, keyPropertyName = itemKeyForIdentifier) => item?.[keyPropertyName] ?? index
		} = {}
	) {
		return items?.map((item, i) => {
			let key = keySolver(item, i) ?? i;
			if(!["string", "number"].includes(typeof key)) {
				console.warn(`key type of "${typeof key}" is probably not supported. Double check and change it's type to either a string or a number.`);
			}

			return (
				<Fragment key={key}>
					{componentSolver(item, i, items)}
				</Fragment>
			)
		})
	}
}
